const PDF_LIGHT_THEME_VARS = {
  '--v-text-base': '#1a1a1a',
  '--v-surface-base': '#ffffff',
  '--v-background-base': '#ffffff',
  '--v-secondary-base': '#424242',
  '--v-primary-base': '#1976d2',
  '--report-table-border-strong': '#444444',
  '--report-table-border-soft': '#444444',
  '--report-label-text': '#1a1a1a',
  '--report-label-bg': '#f0f0f0',
  '--report-cell-bg': '#ffffff',
  '--report-image-border': '#444444'
};

export const PDF_SKIP_PAINT_TAGS = new Set([
  'IMG',
  'SVG',
  'CANVAS',
  'BR',
  'HR',
  'COL',
  'COLGROUP',
  'STYLE',
  'SCRIPT'
]);

/**
 * CSS 색에서 상대 휘도(0~1). 파싱 불가면 null.
 * @param {string} raw
 * @returns {number|null}
 */
export function relativeLuminanceFromCssColor(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s || s === 'transparent' || s === 'inherit' || s === 'none') return null;
  if (s === 'rgba(0, 0, 0, 0)' || s === 'rgba(0,0,0,0)') return null;

  let r;
  let g;
  let b;
  const hex = s.match(/^#([0-9a-f]{3,8})$/i);
  if (hex) {
    let h = hex[1];
    if (h.length === 3 || h.length === 4) {
      h = h
        .slice(0, 3)
        .split('')
        .map((c) => c + c)
        .join('');
    } else {
      h = h.slice(0, 6);
    }
    r = parseInt(h.slice(0, 2), 16);
    g = parseInt(h.slice(2, 4), 16);
    b = parseInt(h.slice(4, 6), 16);
  } else {
    const m = s.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
    if (!m) return null;
    r = Number(m[1]);
    g = Number(m[2]);
    b = Number(m[3]);
  }
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

function setPdfLightCssVars(el) {
  if (!el || !el.style) return;
  Object.keys(PDF_LIGHT_THEME_VARS).forEach((key) => {
    el.style.setProperty(key, PDF_LIGHT_THEME_VARS[key], 'important');
  });
  el.style.setProperty('background-color', '#ffffff', 'important');
  el.style.setProperty('color', '#1a1a1a', 'important');
}

function forceLightClassList(el) {
  if (!el || !el.classList) return;
  el.classList.remove('theme--dark');
  el.classList.add('theme--light');
}

function paintReportPdfLightTree(root) {
  forceLightClassList(root);
  root.classList.add('report-pdf-light-mode');
  setPdfLightCssVars(root);

  if (typeof root.querySelectorAll === 'function') {
    root.querySelectorAll('.theme--dark').forEach(forceLightClassList);
  }

  const walk = (el) => {
    if (!el || el.nodeType !== 1) return;
    const tag = el.tagName;
    if (PDF_SKIP_PAINT_TAGS.has(tag)) return;
    if (typeof el.closest === 'function' && el.closest('svg')) return;
    if (typeof el.closest === 'function' && el.closest('.pdf-page-spacer, .pdf-page-spacer-row')) {
      return;
    }

    if (tag === 'TH') {
      el.style.setProperty('background-color', '#f0f0f0', 'important');
      el.style.setProperty('color', '#1a1a1a', 'important');
      el.style.setProperty('border-color', '#444444', 'important');
    } else if (tag === 'TD') {
      el.style.setProperty('background-color', '#ffffff', 'important');
      el.style.setProperty('color', '#1a1a1a', 'important');
      el.style.setProperty('border-color', '#444444', 'important');
    } else {
      const win = el.ownerDocument && el.ownerDocument.defaultView;
      const cs = win ? win.getComputedStyle(el) : null;
      const bgLum = cs ? relativeLuminanceFromCssColor(cs.backgroundColor) : null;
      const fgLum = cs ? relativeLuminanceFromCssColor(cs.color) : null;
      if (bgLum != null && bgLum < 0.55) {
        el.style.setProperty('background-color', '#ffffff', 'important');
      }
      if (fgLum != null && fgLum > 0.65) {
        el.style.setProperty('color', '#1a1a1a', 'important');
      }
    }

    const { children } = el;
    for (let i = 0; i < children.length; i++) walk(children[i]);
  };

  walk(root);
}

/**
 * 보고서 PDF/인쇄는 A4 용지용이라 앱 테마와 무관하게 밝은 지면·어두운 글자로 고정한다.
 * html2canvas onclone은 화면 computed(다크) 색을 인라인으로 박제하므로, 그 전에 호출해야 한다.
 * @param {ParentNode} root
 */
export function applyReportPdfLightTheme(root) {
  if (!root) return;

  if (root.nodeType === 9) {
    applyReportPdfLightTheme(root.body || root.documentElement);
    return;
  }
  if (root.nodeType !== 1) return;

  const dialogs = [];
  if (root.classList && root.classList.contains('report-dialog')) {
    dialogs.push(root);
  }
  if (typeof root.querySelectorAll === 'function') {
    root.querySelectorAll('.report-dialog').forEach((el) => dialogs.push(el));
  }
  const unique = [...new Set(dialogs)];
  if (unique.length === 0) {
    paintReportPdfLightTree(root);
    return;
  }
  unique.forEach(paintReportPdfLightTree);
}
